import React, { useState } from 'react';
import { IProcessPurchaseOrdersDialogsProperties } from './ProcessPurchaseOrdersDialogs.properties';
import ProcessPurchaseOrdersDialog from './ProcessPurchaseOrdersDialog';
import UpdateDueDatesDialog from './UpdateDueDatesDialog';
import { useProcessPurchaseOrders, useRetrievePurchaseOrdersSummary } from 'api/purchaseOrders/purchaseOrders';
import { usePOContextSelector, usePODispatch } from '../PurchaseOrders.context';
import PrintTransmitPODialog from './PrintTransmitPODialog';
import Authorisation from 'components/common/Authorisation';
import { isScreenRequest } from 'api/utils';
import InternalOrderDialog from 'components/Worksale/BackorderCommitment/InternalOrderDialog';
import moment from 'moment';
import { INotificationFacade } from 'api/swaggerTypes';
import { useSendNotification } from 'api/documentOutput';

const ProcessPurchaseOrdersDialogs = (props: IProcessPurchaseOrdersDialogsProperties) => {
    const { selectedPurchaseOrder, linesRef, forwardedRef, currentUserId } = props;
    const [processPODialogOpen, setProcessPODialogOpen] = React.useState(false);
    const [processAuthorisationDialogOpen, setProcessAuthorisationDialogOpen] = React.useState(false);
    const [authorisationData, setAuthorisationData] = React.useState(null);
    const [updateDueDatesDialogOpen, setUpdateDueDatesDialogOpen] = React.useState<boolean>(false);
    const [printTransmitPODialogOpen, setPrintTransmitPODialogOpen] = React.useState<boolean>(false);
    const [internalOrderNotificationDialogOpen, setInternalOrderNotificationDialogOpen] = React.useState<boolean>(false);
    const [printTransmitPOData, setPrintTransmitPOData] = React.useState(null);
    const [internalOrderNotificationData, setInternalOrderNotificationData] = React.useState(null);
    const processPurchaseOrdersMutation = useProcessPurchaseOrders();
    const [mainContent, setMainContent] = useState({});
    const ProcessPurchaseOrderDetails = usePOContextSelector<'ProcessPurchaseOrderDetails'>((state) => state.ProcessPurchaseOrderDetails);
    const sendNotificationMutation = useSendNotification();
    const contextDispatch = usePODispatch();
    const fetchPOSummary = useRetrievePurchaseOrdersSummary(usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder));

    React.useImperativeHandle(
        forwardedRef,
        () => ({
            process: () => setProcessPODialogOpen(true),
        }),
        []
    );

    const processPurchaseOrders = React.useCallback(
        async (data) => {
            const payload = {
                ...data,
                PurchaseOrder: selectedPurchaseOrder,
                ProcessAction: ProcessPurchaseOrderDetails?.Action,
                UserId: (ProcessPurchaseOrderDetails?.UserId ?? currentUserId) || 0,
                Pin: ProcessPurchaseOrderDetails?.Pin ?? undefined
            };

            const response = await processPurchaseOrdersMutation.mutateAsync(payload);
            setMainContent(payload);

            if (isScreenRequest(response?.Forms, 'SelectDueDateOption')) {
                setProcessPODialogOpen(false);
                setProcessAuthorisationDialogOpen(false);
                setPrintTransmitPODialogOpen(false);
                setUpdateDueDatesDialogOpen(true);

                return;
            }
            if (isScreenRequest(response?.Forms, 'PrintTransmitScreen')) {
                setProcessPODialogOpen(false);
                setProcessAuthorisationDialogOpen(false);
                setUpdateDueDatesDialogOpen(false);
                setPrintTransmitPOData(response?.ProcessPurchaseOrder);
                setPrintTransmitPODialogOpen(true);

                return;
            }

            if (isScreenRequest(response?.Forms, 'AuthorisationScreenRequest')) {
                setProcessPODialogOpen(false);
                setPrintTransmitPODialogOpen(false);
                setUpdateDueDatesDialogOpen(false);
                setAuthorisationData((response?.ProcessPurchaseOrder as any)?.inlineObject);
                setProcessAuthorisationDialogOpen(true);

                return;
            }

            if (response?.Status === true && response?.Forms?.[0]?.MessageType === 'Success') {
                setProcessPODialogOpen(false);
                setProcessAuthorisationDialogOpen(false);
                if ((response?.ProcessPurchaseOrder as any)?.inlineObject?.PromptInternalOrderNotification) {
                    setInternalOrderNotificationData((response?.ProcessPurchaseOrder as any)?.inlineObject?.Notification);
                    setInternalOrderNotificationDialogOpen(true);
                }
                linesRef.current?.refreshDataGrid();
                fetchPOSummary?.refetch();
                contextDispatch({ setProcessPurchaseOrderDetails: { Action: null, UserId: null, Pin: null } });
            }
        },
        [linesRef, fetchPOSummary, ProcessPurchaseOrderDetails]
    );

    const updateDueDatesOnOk = React.useCallback(
        (values) => {
            processPurchaseOrders({
                ...values,
                NewDueDate: moment(values?.NewDueDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                SpecificDueDate: values?.SpecificDueDate ? moment(values?.SpecificDueDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : undefined,
            });
            setUpdateDueDatesDialogOpen(false);
        },
        [processPurchaseOrders]
    );

    const printTransmitPurchaseOrdersOnOk = React.useCallback(
        (values) => {
            processPurchaseOrders({
                ...values
            });
            setPrintTransmitPODialogOpen(false);
        },
        [processPurchaseOrders]
    );

    const sendNotificationonOk = React.useCallback(
        async (data: INotificationFacade) => {
            const response = await sendNotificationMutation.mutateAsync({
                ...data
            });

            if (response?.Status) {
                setInternalOrderNotificationDialogOpen(false);
            }
        },
        []
    );

    return (
        <>
            <ProcessPurchaseOrdersDialog
                open={processPODialogOpen}
                isProcessing={processPurchaseOrdersMutation.isLoading}
                onClose={() => { setProcessPODialogOpen(false); }}
                onOk={processPurchaseOrders}
                linesCount={linesRef?.current?.getLinesCount()}
            />
            <UpdateDueDatesDialog
                open={updateDueDatesDialogOpen}
                data={{}}
                isProcessing={processPurchaseOrdersMutation.isLoading}
                onOk={updateDueDatesOnOk}
                onCancel={() => {
                    setUpdateDueDatesDialogOpen(false);
                }} />
            <PrintTransmitPODialog
                open={printTransmitPODialogOpen}
                data={printTransmitPOData}
                isProcessing={processPurchaseOrdersMutation.isLoading}
                onOk={printTransmitPurchaseOrdersOnOk}
                supplierId={fetchPOSummary?.data?.PurchaseOrderSummary?.inlineObject?.Supplier}
                onCancel={() => {
                    setPrintTransmitPODialogOpen(false);
                }} />

            <Authorisation
                open={processAuthorisationDialogOpen}
                mainContent={mainContent}
                initialValues={authorisationData}
                valuesSchema={{}}
                onOk={processPurchaseOrders}
                onClose={() => setProcessAuthorisationDialogOpen(false)}
                linesRef={linesRef}
            />
            <InternalOrderDialog
                open={internalOrderNotificationDialogOpen}
                value={internalOrderNotificationData}
                valueSchema={{}}
                isProcessing={sendNotificationMutation.isLoading}
                onCancel={() => setInternalOrderNotificationDialogOpen(false)}
                onOk={sendNotificationonOk}
            />
        </>
    );
};

export default ProcessPurchaseOrdersDialogs;
